import type { EmailAddress } from "./types";

const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dayMonth = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });
const full = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
const withYear = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });

/**
 * A list-row date.
 *
 * Time for today, day and month for this year, year beyond that. Scanning a
 * list is a comparison between neighbouring rows, and "14:32" next to "3 Mar"
 * says which is which faster than two identical-looking absolute dates.
 */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return time.format(d);
  if (d.getFullYear() === now.getFullYear()) return dayMonth.format(d);
  return withYear.format(d);
}

export function fullDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : full.format(d);
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["kB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function displayName(a: EmailAddress | undefined): string {
  if (!a) return "";
  return a.name?.trim() || a.email;
}

export function addressList(list: EmailAddress[] | null | undefined, max = 3): string {
  if (!list || list.length === 0) return "";
  const names = list.slice(0, max).map(displayName);
  const rest = list.length - names.length;
  return rest > 0 ? `${names.join(", ")} +${rest}` : names.join(", ");
}

/**
 * Up to two letters for the avatar.
 *
 * Initials rather than a fetched avatar: a Gravatar request would hand a
 * third party the hash of every correspondent's address, which is the same
 * privacy leak as a tracking pixel with a friendlier name.
 */
export function initials(a: EmailAddress | undefined): string {
  const name = displayName(a);
  if (!name) return "?";
  const parts = name.split(/[\s.@_-]+/u).filter(Boolean);
  const first = parts[0]?.[0] ?? name[0] ?? "?";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase();
}

/**
 * A stable hue per address, so the same sender is the same colour everywhere.
 * FNV-1a: not a good hash, but a cheap and well-distributed one, and nothing
 * here depends on it being unpredictable.
 */
export function hueFor(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return Math.abs(h) % 360;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
